import { useCallback } from 'react';

import { NotificationSeverity } from '@/components/notifications/Notification';
import { useNotification } from '@/hooks';
import { appUrl, getErrorMessage } from '@/utils';
import { toFullMobilePhone } from '@/utils/phoneNumber';

import { EditUserPersonalSchema, UserApi } from '../models';
import {
  useDeleteUserMutation,
  useSendInvitationMutation,
  useSendOtpByEmailMutation,
  useUpdateUserMutation,
} from '../services';

const useUserActions = () => {
  const { showNotification } = useNotification();

  const [updateUser, { isLoading: isUpdatingUser }] = useUpdateUserMutation();
  const [deleteUser, { isLoading: isDeletingUser }] = useDeleteUserMutation();
  const [sendInvitation, { isLoading: isSendingInvitation }] =
    useSendInvitationMutation();
  const [sendOtpByEmail, { isLoading: isSendingOtpByEmail }] =
    useSendOtpByEmailMutation();

  const onUpdateUser = useCallback(
    async (
      userId: string,
      data: EditUserPersonalSchema,
      onSuccess?: () => void
    ): Promise<boolean> => {
      try {
        await updateUser({
          id: userId,
          body: {
            firstName: data.firstName.trim(),
            lastName: data.lastName.trim(),
            email: data.email.trim().toLowerCase(),
            phoneNumber: toFullMobilePhone(data.phoneNumber),
          },
        }).unwrap();
        showNotification(
          'Usuario actualizado correctamente',
          NotificationSeverity.success
        );
        onSuccess?.();
        return true;
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(
          error,
          'Ocurrió un error al actualizar el usuario. Inténtalo nuevamente.'
        );
        showNotification(errorMessage, NotificationSeverity.error);
        return false;
      }
    },
    [updateUser, showNotification]
  );

  const onDeleteUser = useCallback(
    async (id: string) => {
      try {
        await deleteUser({ id }).unwrap();
        showNotification(
          'Usuario eliminado correctamente',
          NotificationSeverity.success
        );
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(
          error,
          'Ocurrió un error al eliminar el usuario. Inténtalo nuevamente.'
        );
        showNotification(errorMessage, NotificationSeverity.error);
      }
    },
    [deleteUser, showNotification]
  );

  const onSendInvitation = useCallback(
    async (user: UserApi): Promise<boolean> => {
      try {
        await sendInvitation({
          userId: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          phoneNumber: user.phoneNumber,
          invitationLink: appUrl(`/register/${user.id}`),
        }).unwrap();

        showNotification(
          'Invitación enviada correctamente',
          NotificationSeverity.success
        );
        return true;
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(
          error,
          'Ocurrió un error al enviar la invitación. Inténtalo nuevamente.'
        );
        showNotification(errorMessage, NotificationSeverity.error);
        return false;
      }
    },
    [sendInvitation, showNotification]
  );

  const onSendOtpByEmail = useCallback(
    async (userId: string): Promise<boolean> => {
      try {
        await sendOtpByEmail({ userId }).unwrap();
        showNotification(
          'Código OTP enviado por email correctamente',
          NotificationSeverity.success
        );
        return true;
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(
          error,
          'Ocurrió un error al enviar el código OTP por email. Inténtalo nuevamente.'
        );
        showNotification(errorMessage, NotificationSeverity.error);
        return false;
      }
    },
    [sendOtpByEmail, showNotification]
  );

  return {
    onUpdateUser,
    onDeleteUser,
    onSendInvitation,
    onSendOtpByEmail,
    isUpdatingUser,
    isDeletingUser,
    isSendingInvitation,
    isSendingOtpByEmail,
    isLoading:
      isUpdatingUser ||
      isDeletingUser ||
      isSendingInvitation ||
      isSendingOtpByEmail,
  };
};

export default useUserActions;
